import React, { FC, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Helmet } from 'react-helmet';
import { AppBar, Link, Toolbar, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useGetPublicContentQuery } from 'api/public';
import { ClientLogo, Dimensions, Layouts, Loaders } from 'components';
import { usePublicClient, useNotify } from 'hooks';
import { Events } from 'analytics';
import * as Links from 'Links';
import { ContentProvider, DimensionsProvider, QueryVariablesProvider } from 'providers';
import * as LayoutComponents from 'startup/public/Components';
import { FACEBOOK_APP_ID } from 'settings';

import { getOpenGraphTags, getPublicLayout, isTextOnly } from '../helpers';

const useStyles = makeStyles(theme => ({
  logo: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
}));

interface IRouteParams {
  content_id: string;
}

const PublicPermalink: FC = () => {
  const classes = useStyles();
  const notify = useNotify();
  const client = usePublicClient();
  const { t } = useTranslation();
  const { content_id } = useParams<IRouteParams>();

  const { data, isLoading } = useGetPublicContentQuery(
    { content_id, client_id: client.id },
    {
      onError: notify.queryError,
    }
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('es_source')) {
      window?.analytics?.track(Events.RepostClicked, {
        referral_source: searchParams.get('es_source'),
      });
    }

    window?.analytics?.track(Events.PublicPostImpression);
  }, []);

  if (isLoading) {
    return <Loaders.Standard />;
  }

  // Avoid showing the visitor an empty page
  const isBlankLayout = !client.public_post_details && isTextOnly(data?.getPublicContent);

  if (!data?.getPublicContent || isBlankLayout) {
    return <LayoutComponents.AuthLayout />;
  }

  const { title, description, imageUrl, imageHeight, imageWidth } = getOpenGraphTags(
    data.getPublicContent
  );

  function handleTracking(property: 'customer' | 'everyonesocial') {
    window?.analytics?.track(Events.PublicPostLogoClicked, {
      customer: property === 'customer',
      everyonesocial: property === 'everyonesocial',
    });
  }

  function renderContent() {
    const { getPublicContent } = data;
    const ContentComponent = getPublicLayout(getPublicContent);

    return (
      <QueryVariablesProvider value={{ content_id }}>
        <ContentProvider value={{ content: getPublicContent }}>
          <Dimensions>
            {dimensions => (
              <DimensionsProvider value={{ dimensions }}>
                <ContentComponent />
              </DimensionsProvider>
            )}
          </Dimensions>
        </ContentProvider>
      </QueryVariablesProvider>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:height" content={imageHeight} />
        <meta property="og:image:width" content={imageWidth} />
        <meta property="fb:app_id" content={FACEBOOK_APP_ID} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:site" content="@everyonesocial" />
      </Helmet>
      <AppBar>
        <Toolbar>
          <Typography
            onClick={() => handleTracking('customer')}
            className={classes.logo}
            component={Links.Index}
            color="inherit"
          >
            <ClientLogo client={client} disableMargin height={24} />
          </Typography>
          <Link
            onClick={() => handleTracking('everyonesocial')}
            href="https://everyonesocial.com"
            target="_blank"
            rel="noopener noreferrer"
            color="textSecondary"
          >
            {t('components:poweredBy')}
          </Link>
        </Toolbar>
      </AppBar>
      <Layouts.App1Column
        mobileSize={10}
        tabletSize={8}
        desktopSize={6}
        main={<Layouts.AppMain1Column main={renderContent()} />}
      />
    </Fragment>
  );
};

export default PublicPermalink;
